// Stage 1 handling of the business's own priced notes (D6, Peter 2026-10-06:
// "estimator notes feed the plan, with their prices removed"; CN1, Peter
// 2026-10-13: "file names shouldnt dictate anything. only the contents.").
//
// Before D6 every plain-text file that was not an RFQ was appended, as typed,
// to Inspector Notes (formData.notes) and kept as a 'notes_source' document,
// so the estimator's notes reached every stage with their prices. Until CN1
// the notes were recognised by their file name, so the same notes saved as
// "Notes.txt" still did. Now, by the text alone:
//   - an RFQ or scope document keeps its route as a document;
//   - estimator notes become an 'estimator_notes' document whose text has its
//     prices removed, and are NOT appended to Inspector Notes;
//   - any other text that reads as priced working notes (a report or a plan
//     by the shared rules, for instance) still goes to Inspector Notes, but
//     with its prices removed first (stage1InspectorNotesText);
//   - every other text (site notes, visit notes, a figure mentioned in
//     passing) keeps today's route: appended to Inspector Notes as written.
import { stripEstimatorNotePrices } from '../utils/estimator-notes-price-strip';
import { detectEstimatorPricedNotes } from '../utils/estimator-notes-detector';
import { classifyDocumentType, loadActiveBusinessIdentity } from './document-type-classifier';
import { buildStage1DeterministicClassificationMetadata } from './stage1-batch-classification';

export type Stage1TextFileRoute = 'document' | 'estimator_notes' | 'inspector_notes';

export function stage1TextFileRoute(classification: any): Stage1TextFileRoute {
    const kind = String(classification?.class || '');
    if (kind === 'rfq' || kind === 'scope_of_works') return 'document';
    if (kind === 'estimator_notes') return 'estimator_notes';
    return 'inspector_notes';
}

/**
 * The text the Assistant may read from a file in the project folder
 * (read_project_file, read_sync_folder_file). Any file, text, PDF or Word,
 * whose text reads as the builder's priced working notes loses its price
 * lines, unless it reads as an RFQ or a scope of works (then it is the
 * request, read with its prices). Every other file is returned as written.
 * The path gives the file's name; neither the name nor the folder decides
 * whether prices are removed or whether the file is the request.
 */
export async function estimatorNotesFileText(relativePath: unknown, text: unknown): Promise<string> {
    const value = typeof text === 'string' ? text : '';
    if (!detectEstimatorPricedNotes(value).isEstimatorNotes) return value;
    const path = typeof relativePath === 'string' ? relativePath : '';
    // The last non-empty segment, so 'quote notes.txt/' still has a name.
    const name = path.split(/[\\/]/).filter(Boolean).pop() || '';
    const identity = await loadActiveBusinessIdentity();
    const classification = classifyDocumentType(name, value, { ...identity });
    return stage1TextFileRoute(classification) === 'document' ? value : stripEstimatorNotePrices(value);
}

/**
 * The text an imported file adds to Inspector Notes: prices removed when the
 * text reads as the builder's priced working notes, as written otherwise.
 * Whatever the file is called.
 */
export function stage1InspectorNotesText(text: unknown): string {
    const value = typeof text === 'string' ? text : '';
    return detectEstimatorPricedNotes(value).isEstimatorNotes ? stripEstimatorNotePrices(value) : value;
}

export interface Stage1EstimatorNotesDocumentInput {
    id: string;
    name: string;
    text: string;
    size?: number;
    mimeType?: string | null;
    classification: any;
}

/**
 * The notes as written, prices included, for the Quote Brain only (slice EVID,
 * DESIGN.md 4.4). They ride the row as a Blob, which no prompt builder reads
 * (a Blob has no text without an await, and JSON writes it as {}). The save
 * path moves it to the local blob store (rawNotesBlobId), then uploads it to
 * the brain-only server lane `project_builder_notes` (rawNotesS3Key), which no
 * download route serves. Nothing else may name these fields: the test
 * stage1-raw-notes-brain-only.test.ts holds that list.
 */
export function rawEstimatorNotesBlob(text: unknown): Blob | null {
    // Control characters other than tab and line breaks carry nothing in notes,
    // and the server's text rule refuses them (containsDisallowedTextControlBytes).
    // eslint-disable-next-line no-control-regex
    const value = (typeof text === 'string' ? text : '').replace(/[\u0000-\u0008\u000e-\u001f]/g, '');
    if (!value.trim() || typeof Blob === 'undefined') return null;
    return new Blob([value], { type: 'text/plain' });
}

/**
 * The document row for an estimator-notes text file. Its stored text has the
 * prices removed, so no later prompt, preview or AI pass can read them. The
 * operator's own file keeps its prices, and so does the brain-only copy
 * (rawNotesBlob above).
 */
export function buildStage1EstimatorNotesDocument({ id, name, text, size, mimeType, classification }: Stage1EstimatorNotesDocumentInput): any {
    const ext = (String(name || '').split('.').pop() || 'txt').toLowerCase();
    const stripped = stripEstimatorNotePrices(text);
    const rawNotesBlob = rawEstimatorNotesBlob(text);
    const contentType = mimeType || (ext === 'md' ? 'text/markdown' : 'text/plain');
    return {
        id,
        name,
        originalName: name,
        ext,
        type: 'estimator_notes',
        fileKind: ext === 'md' ? 'markdown' : 'text',
        content: stripped,
        textContent: stripped,
        contentType,
        mimeType: contentType,
        size,
        addedAt: new Date().toISOString(),
        aiClassification: 'estimator_notes',
        estimatorNotesPricesRemoved: true,
        ...(rawNotesBlob ? { rawNotesBlob } : {}),
        ...buildStage1DeterministicClassificationMetadata(classification),
    };
}
